import React, { useState } from 'react';
import { FORMSPREE_ENDPOINT } from '../config/launch';
import { getSource, trackEvent } from '../lib/analytics';
import Icon from './Icon';

// With no FORMSPREE_ENDPOINT set, the form falls back to opening the visitor's
// mail client with the message prefilled.
const CONTACT_EMAIL = 'hello@sidwell.ai';

const FIELD_LABEL = 'mb-1.5 block text-[14px] font-semibold';

function buildMailto({ name, email, company, message }) {
  const subject = encodeURIComponent(`Demo request — ${company || name || 'Sidwell'}`);
  const body = encodeURIComponent(
    [
      `Name: ${name}`,
      `Email: ${email}`,
      company && `Company: ${company}`,
      '',
      message,
    ]
      .filter(Boolean)
      .join('\n')
  );
  return `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
}

function RequestDemoForm() {
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });

  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!FORMSPREE_ENDPOINT) {
      trackEvent('demo_request_mailto');
      window.location.href = buildMailto(form);
      return;
    }

    setStatus('submitting');
    try {
      const body = new FormData(e.target);
      const source = getSource();
      if (source) body.append('source', source);
      body.append('page', window.location.pathname);
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      });
      if (!res.ok) throw new Error('Request failed');
      trackEvent('demo_request_submitted');
      setStatus('success');
      setForm({ name: '', email: '', company: '', message: '' });
    } catch (err) {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="statement px-6 py-10 shadow-raised sm:px-9" role="status">
        <Icon name="check" size={28} className="mb-4 text-patina" />
        <h3 className="type-h3 mb-2">Thanks — we&rsquo;ll be in touch.</h3>
        <p className="text-glass">We read every request. Expect a reply within two business days.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="statement px-6 pb-7 pt-6 text-left shadow-raised sm:px-9 sm:pb-9 sm:pt-8">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <label className="block">
          <span className={FIELD_LABEL}>Name</span>
          <input className="field" type="text" name="name" autoComplete="name" required value={form.name} onChange={update} />
        </label>
        <label className="block">
          <span className={FIELD_LABEL}>Work email</span>
          <input className="field" type="email" name="email" autoComplete="email" required value={form.email} onChange={update} />
        </label>
      </div>
      <label className="mt-5 block">
        <span className={FIELD_LABEL}>Company</span>
        <input className="field" type="text" name="company" autoComplete="organization" value={form.company} onChange={update} />
      </label>
      <label className="mt-5 block">
        <span className={FIELD_LABEL}>What do you want to ask your data?</span>
        <textarea className="field min-h-[104px] resize-y" name="message" rows={3} value={form.message} onChange={update} />
      </label>

      {status === 'error' && (
        <p role="alert" className="mt-4 text-[15px] text-oxide">
          Something went wrong. Email us directly at{' '}
          <a className="underline underline-offset-4" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3">
        <button type="submit" disabled={status === 'submitting'} className="btn-primary w-full sm:w-auto">
          {status === 'submitting' ? 'Sending…' : 'Request demo'}
        </button>
        <p className="text-[14px] text-glass">No sales sequence. We reply from a real inbox.</p>
      </div>
    </form>
  );
}

export default RequestDemoForm;
